/* eslint-disable react/no-danger */
import type { FC } from 'react';
import clsx from 'clsx';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import type { CurrencyCode } from '@fragment/types';

import { formatAmountCurrency } from '../../utils/currency';
import { formatAmount } from '../../utils/formatAmount';

export { formatAmount } from '../../utils/formatAmount';

export type AmountProps = {
  amount: string;
  currency: {
    code: CurrencyCode;
    customCode?: string | null;
    name: string;
    precision: number;
  };
  showCurrency?: boolean;
};

export const Amount: FC<AmountProps> = ({
  amount,
  currency,
  showCurrency = true,
}) => {
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [isTruncated, setIsTruncated] = useState<boolean>(true);
  const ref = useRef<HTMLSpanElement>(null);
  const onHover = useCallback(() => {
    setIsTruncated(false);
  }, []);
  const onLeave = useCallback(() => {
    setIsTruncated(true);
    // Clear the window selection only if this component set
    // the selection.
    if (ref.current !== null && isSelected) {
      window.getSelection()?.empty();
      setIsSelected(false);
    }
  }, [ref, isSelected]);
  const clickHandler = useCallback(() => {
    if (ref.current !== null) {
      window.getSelection()?.selectAllChildren(ref.current);
      setIsSelected(true);
    }
  }, [ref]);

  const formattedAmount = useMemo(
    () =>
      formatAmount({
        amount,
        precision: currency.precision,
        isTruncated,
      }),
    [amount, currency, isTruncated]
  );
  const suffix = formatAmountCurrency(currency);
  return (
    <span>
      <span
        data-testid="amount"
        role="presentation"
        ref={ref}
        onClick={clickHandler}
        onMouseOver={onHover}
        onFocus={onHover}
        onMouseOut={onLeave}
        onBlur={onHover}
        className={clsx('flex', 'flex-row-reverse', 'inline-flex')}
        dangerouslySetInnerHTML={{
          __html: formattedAmount,
        }}
      />
      {showCurrency && ` ${suffix}`}
    </span>
  );
};
